/**
 * React Query hooks for the survey system.
 *
 * These go through `surveysClient` rather than the shared `useApiQuery`, because every call
 * has to be able to fall back to the mock while the endpoints are still being built. The
 * query keys, `enabled` gating and envelope-unwrapping otherwise match the rest of lib/api.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useAuth } from "../../context/auth-context";
import { apiQueryKey } from "./queryKeys";
import {
  fetchSurvey,
  fetchSurveyResponses,
  fetchSurveySummary,
  fetchSurveys,
  fetchSurveysMetrics,
  type ListSurveysParams,
} from "./surveysClient";
import type {
  SurveyApi,
  SurveyResponsesPayload,
  SurveySummaryPayload,
  SurveysListPayload,
  SurveysMetricsPayload,
} from "./surveys";

export const SURVEYS_TABLE_LIMIT = 15;
export const SURVEY_RESPONSES_LIMIT = 20;

export type SurveyListFilters = {
  page: number;
  limit?: number;
  search?: string;
  /** "" or omitted = every status. */
  status?: string;
  enabled?: boolean;
};

export function useSurveysQuery(filters: SurveyListFilters) {
  const { isAuthenticated } = useAuth();
  const {
    page,
    limit = SURVEYS_TABLE_LIMIT,
    search,
    status = "",
    enabled = true,
  } = filters;

  const params: ListSurveysParams = {
    page,
    limit,
    search: search?.trim() || undefined,
    status: status || undefined,
  };

  return useQuery<SurveysListPayload, Error>({
    queryKey: apiQueryKey("surveys", {
      page,
      limit,
      search: params.search ?? null,
      status: status || null,
    }),
    queryFn: async ({ signal }) => (await fetchSurveys(params, signal)).data,
    enabled: Boolean(isAuthenticated) && enabled,
  });
}

export function useSurveysMetricsQuery(enabled = true) {
  const { isAuthenticated } = useAuth();

  return useQuery<SurveysMetricsPayload, Error>({
    queryKey: apiQueryKey("surveys-metrics"),
    queryFn: async ({ signal }) => (await fetchSurveysMetrics(signal)).data,
    enabled: Boolean(isAuthenticated) && enabled,
  });
}

export function useSurveyDetailQuery(surveyId: string | undefined) {
  const { isAuthenticated } = useAuth();

  return useQuery<SurveyApi, Error>({
    queryKey: apiQueryKey("survey", surveyId ?? ""),
    queryFn: async ({ signal }) =>
      (await fetchSurvey(surveyId as string, signal)).data,
    enabled: Boolean(isAuthenticated) && Boolean(surveyId),
  });
}

export function useSurveySummaryQuery(
  surveyId: string | undefined,
  enabled = true
) {
  const { isAuthenticated } = useAuth();

  return useQuery<SurveySummaryPayload, Error>({
    queryKey: apiQueryKey("survey-summary", surveyId ?? ""),
    queryFn: async ({ signal }) =>
      (await fetchSurveySummary(surveyId as string, signal)).data,
    enabled: Boolean(isAuthenticated) && Boolean(surveyId) && enabled,
  });
}

export type SurveyResponsesFilters = {
  page: number;
  limit?: number;
  search?: string;
  status?: string;
  enabled?: boolean;
};

export function useSurveyResponsesQuery(
  surveyId: string | undefined,
  filters: SurveyResponsesFilters
) {
  const { isAuthenticated } = useAuth();
  const {
    page,
    limit = SURVEY_RESPONSES_LIMIT,
    search,
    status = "",
    enabled = true,
  } = filters;

  const params: ListSurveysParams = {
    page,
    limit,
    search: search?.trim() || undefined,
    status: status || undefined,
  };

  return useQuery<SurveyResponsesPayload, Error>({
    queryKey: apiQueryKey("survey-responses", surveyId ?? "", {
      page,
      limit,
      search: params.search ?? null,
      status: status || null,
    }),
    queryFn: async ({ signal }) =>
      (await fetchSurveyResponses(surveyId as string, params, signal)).data,
    enabled: Boolean(isAuthenticated) && Boolean(surveyId) && enabled,
  });
}

/**
 * Invalidates every survey query after a mutation. Broad on purpose: publishing or editing
 * a survey moves the list counts, the metric cards and the detail header at once.
 */
export function useInvalidateSurveys() {
  const queryClient = useQueryClient();

  return useCallback(
    (surveyId?: string) => {
      const keys = [
        apiQueryKey("surveys"),
        apiQueryKey("surveys-metrics"),
        ...(surveyId
          ? [
              apiQueryKey("survey", surveyId),
              apiQueryKey("survey-summary", surveyId),
              apiQueryKey("survey-responses", surveyId),
            ]
          : []),
      ];
      keys.forEach((queryKey) =>
        queryClient.invalidateQueries({ queryKey, exact: false })
      );
    },
    [queryClient]
  );
}
